import React, { useRef, useEffect, useState, useCallback } from 'react';
import '../../assets/css/Modals/compare.scss';
import { useTranslation } from '../../contexts/TranslationContext';
import { getFullImageUrl, getThumbnailUrl } from '../../utils/imageUrlBuilder';

// Defined outside Compare so its identity is stable across re-renders.
// React.memo prevents re-render unless items/activeIndex/isSource changes.
const ThumbnailRow = React.memo(({ items, activeIndex, isSource, onSelect, getThumbnailUrl }) => (
    <div className="thumbnail-list">
        {items?.Images?.length > 0 ? items.Images.map((image, idx) => (
            <button key={idx}
                className={`thumbnail-btn${activeIndex === idx ? ' active' : ''}`}
                onClick={() => onSelect(isSource, idx)}
                title={`Image ${idx + 1}`}>
                <img src={getThumbnailUrl(image)} alt={`Thumb ${idx + 1}`} className="thumbnail-image"
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                    <i className="fa fa-image" /><span>{idx + 1}</span>
                </div>
            </button>
        )) : <span className="no-images">&mdash;</span>}
    </div>
));

/**
 * Compare Modal component for side-by-side comparison of footwear items.
 * Features: per-image toolbar (zoom/rotate/contrast/RGB), overlap mode with
 * opacity slider, per-image drag-move with lock toggle.
 */
const Compare = ({ isOpen, onClose, resultItem, probeItem }) => {
    const { language } = useTranslation();

    //  Canvas refs 
    const sourceCanvasRef = useRef(null);
    const targetCanvasRef = useRef(null);
    const overlapCanvasRef = useRef(null);

    //  Loading
    const [sourceLoading, setSourceLoading] = useState(false);
    const [targetLoading, setTargetLoading] = useState(false);

    //  View mode: 'side-by-side' | 'overlap' 
    const [viewMode, setViewMode] = useState('side-by-side');

    //  Image selection 
    const [sourceImageIndex, setSourceImageIndex] = useState(0);
    const [targetImageIndex, setTargetImageIndex] = useState(0);

    //  Per-image transform state
    // Each holds: { scale, rotation, contrast, brightness, r, g, b, offsetX, offsetY, moveLocked }
    const defaultTransform = () => ({ scale: 1, rotation: 0, contrast: 100, brightness: 100, r: 0, g: 0, b: 0, offsetX: 0, offsetY: 0, moveLocked: false });
    const [sourceTransform, setSourceTransform] = useState(defaultTransform());
    const [targetTransform, setTargetTransform] = useState(defaultTransform());

    //  Overlap opacity (0=source only, 100=target only)
    const [overlapOpacity, setOverlapOpacity] = useState(50);

    //  DOM img refs (like Standardize's imgTempRef) — the browser keeps pixel data in the
    //  <img> element after the first load; zoom/rotate/contrast redraws read from this
    //  element directly, with zero network requests.
    const sourceImgRef = useRef(null);
    const targetImgRef = useRef(null);

    //  Drag state refs (avoid re-render on every mousemove)
    const dragState = useRef({ dragging: false, which: null, startX: 0, startY: 0, startOffX: 0, startOffY: 0 });

    // Transform refs — always hold the latest value so the async img.onload callback
    // never draws with a stale transform captured at call time.
    const sourceTransformRef = useRef(sourceTransform);
    sourceTransformRef.current = sourceTransform;
    const targetTransformRef = useRef(targetTransform);
    targetTransformRef.current = targetTransform;

    // Image URL helpers — delegated to shared utility (src/utils/imageUrlBuilder.js)
    const getImageUrl = useCallback(getFullImageUrl, []);
    const getImageThumbnailUrl = useCallback(getThumbnailUrl, []);
    //  Core draw helpers 
    // Draws one image (already a loaded Image object) onto a canvas with all transforms.
    const drawImageOnCanvas = useCallback((canvas, img, transform, setLoadingState) => {
        if (!canvas || !img) { setLoadingState && setLoadingState(false); return; }
        const ctx = canvas.getContext('2d');
        const { scale, rotation, contrast, brightness, r, g, b, offsetX, offsetY } = transform;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.filter = `contrast(${contrast}%) brightness(${brightness}%)`;

        const canvasAspect = canvas.width / canvas.height;
        const imgAspect = img.naturalWidth / img.naturalHeight;
        let baseW, baseH;
        if (!imgAspect || isNaN(imgAspect)) { setLoadingState && setLoadingState(false); return; }
        if (imgAspect > canvasAspect) { baseW = canvas.width; baseH = canvas.width / imgAspect; }
        else { baseH = canvas.height; baseW = canvas.height * imgAspect; }
        const drawW = baseW * scale;
        const drawH = baseH * scale;

        ctx.save();
        ctx.translate(canvas.width / 2 + offsetX, canvas.height / 2 + offsetY);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();
        ctx.filter = 'none';

        if (r !== 0 || g !== 0 || b !== 0) {
            const clamp = (v) => Math.max(0, Math.min(255, v));
            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            ctx.fillStyle = `rgb(${clamp(255 + r)},${clamp(255 + g)},${clamp(255 + b)})`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
        }

        setLoadingState && setLoadingState(false);
    }, []);

    // Mirrors Standardize's imgTempRef pattern:
    //   • img is a hidden DOM <img> element (rendered in JSX, ref-tracked)
    //   • img.complete && img.naturalWidth > 0  → image pixel data is in memory, just redraw
    //   • img.src === url                       → it's the same image, no fetch needed
    //   • Only when URL changes do we assign img.src and wait for onload
    //   • getTransform() is called inside onload to always get the latest transform (not stale)
    const redrawOrLoad = useCallback((img, canvas, url, getTransform, setLoadingState) => {
        if (!url || !canvas || !img) { setLoadingState && setLoadingState(false); return; }

        const doDraw = () => {
            // Sync canvas size to its CSS layout size (handles canvas remount on view mode switch)
            const w = canvas.offsetWidth, h = canvas.offsetHeight;
            if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
                canvas.width = w; canvas.height = h;
            }
            drawImageOnCanvas(canvas, img, getTransform(), setLoadingState);
        };

        if (img.complete && img.naturalWidth > 0 && img.src === url) {
            // Image already in DOM element memory — pure canvas redraw, zero network request
            doDraw();
        } else {
            // New URL: set src once; browser fetches (and caches) it, then draws
            setLoadingState && setLoadingState(true);
            img.onload = doDraw;
            img.onerror = () => {
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = '#f5f5f5'; ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = '#666'; ctx.font = '14px Arial'; ctx.textAlign = 'center';
                ctx.fillText('Image load failed', canvas.width / 2, canvas.height / 2);
                setLoadingState && setLoadingState(false);
            };
            img.src = url;
        }
    }, [drawImageOnCanvas]);

    //  Redraw overlap canvas
    const drawOverlap = useCallback(() => {
        const canvas = overlapCanvasRef.current;
        if (!canvas) return;
        canvas.width = canvas.offsetWidth || canvas.width;
        canvas.height = canvas.offsetHeight || canvas.height;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);

        const sourceAlpha = (100 - overlapOpacity) / 100;
        const targetAlpha = overlapOpacity / 100;

        const drawLayer = (imgRefObj, t, alpha) => {
            if (!imgRefObj.current) return;
            const img = imgRefObj.current;
            const canvasAspect = canvas.width / canvas.height;
            const imgAspect = img.naturalWidth / img.naturalHeight;
            if (!imgAspect || isNaN(imgAspect)) return;
            let baseW, baseH;
            if (imgAspect > canvasAspect) { baseW = canvas.width; baseH = canvas.width / imgAspect; }
            else { baseH = canvas.height; baseW = canvas.height * imgAspect; }
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.filter = `contrast(${t.contrast}%) brightness(${t.brightness}%)`;
            ctx.translate(canvas.width / 2 + t.offsetX, canvas.height / 2 + t.offsetY);
            ctx.rotate((t.rotation * Math.PI) / 180);
            ctx.drawImage(img, -(baseW * t.scale) / 2, -(baseH * t.scale) / 2, baseW * t.scale, baseH * t.scale);
            ctx.restore();
            ctx.filter = 'none';
        };

        drawLayer(sourceImgRef, sourceTransform, sourceAlpha);
        drawLayer(targetImgRef, targetTransform, targetAlpha);
    }, [overlapOpacity, sourceTransform, targetTransform]);

    //  Transform helpers
    const defaultTransformFn = () => ({ scale: 1, rotation: 0, contrast: 100, brightness: 100, r: 0, g: 0, b: 0, offsetX: 0, offsetY: 0, moveLocked: false });
    const updateTransform = (isSource, patch) => {
        if (isSource) setSourceTransform(prev => ({ ...prev, ...patch }));
        else setTargetTransform(prev => ({ ...prev, ...patch }));
    };
    const resetTransform = (isSource) => {
        if (isSource) setSourceTransform(defaultTransformFn());
        else setTargetTransform(defaultTransformFn());
    };

    const handleWheelZoom = (e, isSource) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const t = isSource ? sourceTransform : targetTransform;
        updateTransform(isSource, { scale: Math.min(Math.max(t.scale + delta, 0.1), 10) });
    };

    //  Drag / Move handlers 
    const handleMouseDown = (e, which) => {
        const t = which === 'source' ? sourceTransform : targetTransform;
        if (t.moveLocked) return;
        dragState.current = { dragging: true, which, startX: e.clientX, startY: e.clientY, startOffX: t.offsetX, startOffY: t.offsetY };
    };
    const handleMouseMove = useCallback((e) => {
        const ds = dragState.current;
        if (!ds.dragging) return;
        const dx = e.clientX - ds.startX;
        const dy = e.clientY - ds.startY;
        if (ds.which === 'source') setSourceTransform(prev => ({ ...prev, offsetX: ds.startOffX + dx, offsetY: ds.startOffY + dy }));
        else setTargetTransform(prev => ({ ...prev, offsetX: ds.startOffX + dx, offsetY: ds.startOffY + dy }));
    }, []);
    const handleMouseUp = useCallback(() => { dragState.current.dragging = false; }, []);

    //  Image selection
    const handleImageSelect = useCallback((isSource, imageIndex) => {
        if (isSource) setSourceImageIndex(imageIndex);
        else setTargetImageIndex(imageIndex);
    }, []);

    //  Effects
    // Source: img.complete + img.src === url → redraw only (no fetch); otherwise set src once
    useEffect(() => {
        if (!isOpen || !resultItem?.Images) return;
        const canvas = sourceCanvasRef.current;
        const img = sourceImgRef.current;
        if (!canvas || !img) return;
        const url = getImageUrl(resultItem.Images[sourceImageIndex]);
        redrawOrLoad(img, canvas, url, () => sourceTransformRef.current, setSourceLoading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceImageIndex, sourceTransform, viewMode, isOpen, resultItem]);

    // Target: same strategy — DOM img element as cache, no re-fetch on transform changes
    useEffect(() => {
        if (!isOpen || !probeItem?.Images) return;
        const canvas = targetCanvasRef.current;
        const img = targetImgRef.current;
        if (!canvas || !img) return;
        const url = getImageUrl(probeItem.Images[targetImageIndex]);
        redrawOrLoad(img, canvas, url, () => targetTransformRef.current, setTargetLoading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetImageIndex, targetTransform, viewMode, isOpen, probeItem]);

    // Redraw overlap when anything changes
    useEffect(() => {
        if (viewMode === 'overlap') drawOverlap();
    }, [viewMode, drawOverlap]);

    // Global drag listeners
    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // Reset all state on open
    useEffect(() => {
        if (!isOpen) return;
        setSourceImageIndex(0); setTargetImageIndex(0);
        setSourceTransform(defaultTransformFn()); setTargetTransform(defaultTransformFn());
        setOverlapOpacity(50); setViewMode('side-by-side');
        setOpenMenu(null);
        // sourceImgRef / targetImgRef point to DOM <img> elements in JSX.
        // When isOpen toggles false the whole component returns null — those DOM nodes
        // are unmounted, so on next open they start with empty src / complete=false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // ── Open menu (null | 'source' | 'target') ───────────────────────────────
    const [openMenu, setOpenMenu] = useState(null);
    const ctrlPanelRef = useRef(null);

    // Close popup when clicking outside
    useEffect(() => {
        if (!openMenu) return;
        const handler = (e) => {
            if (ctrlPanelRef.current?.contains(e.target)) return;
            if (e.target.closest?.('.ctrl-fab')) return;
            setOpenMenu(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openMenu]);

    //  Render guard 
    if (!isOpen) return null;

    // ── ControlPanel popup (floating, per panel) ─────────────────────────────
    const ControlPanel = ({ isSource }) => {
        const panelKey = isSource ? 'source' : 'target';
        const t = isSource ? sourceTransform : targetTransform;
        const set = (patch) => updateTransform(isSource, patch);
        return (
            <div
                className="ctrl-panel"
                ref={openMenu === panelKey ? ctrlPanelRef : null}
                onMouseDown={e => e.stopPropagation()}
                data-testid={`ctrl-panel-${panelKey}`}>

                {/* Zoom */}
                <div className="ctrl-section">
                    <div className="ctrl-section__title">
                        <i className="fa fa-search" aria-hidden="true" /> Zoom
                    </div>
                    <div className="ctrl-row">
                        <button className="ctrl-btn" aria-label="Zoom Out"
                            onClick={() => set({ scale: Math.max(t.scale - 0.2, 0.1) })}>
                            <i className="fa fa-minus" aria-hidden="true" />
                        </button>
                        <span className="ctrl-value">{Math.round(t.scale * 100)}%</span>
                        <button className="ctrl-btn" aria-label="Zoom In"
                            onClick={() => set({ scale: Math.min(t.scale + 0.2, 10) })}>
                            <i className="fa fa-plus" aria-hidden="true" />
                        </button>
                        <button className="ctrl-btn ctrl-btn--ghost" aria-label="Reset Zoom"
                            onClick={() => set({ scale: 1 })}>
                            <i className="fa fa-undo" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                {/* Rotate */}
                <div className="ctrl-section">
                    <div className="ctrl-section__title">
                        <i className="fa fa-rotate-right" aria-hidden="true" /> Rotate
                    </div>
                    <div className="ctrl-row">
                        <button className="ctrl-btn" aria-label="Rotate Left"
                            onClick={() => set({ rotation: t.rotation - 90 })}>
                            <i className="fa fa-rotate-left" aria-hidden="true" />
                        </button>
                        <span className="ctrl-value">{((t.rotation % 360) + 360) % 360}&deg;</span>
                        <button className="ctrl-btn" aria-label="Rotate Right"
                            onClick={() => set({ rotation: t.rotation + 90 })}>
                            <i className="fa fa-rotate-right" aria-hidden="true" />
                        </button>
                        <button className="ctrl-btn ctrl-btn--ghost" aria-label="Reset Rotation"
                            onClick={() => set({ rotation: 0 })}>
                            <i className="fa fa-undo" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                {/* Contrast */}
                <div className="ctrl-section">
                    <div className="ctrl-section__title">
                        Contrast <span className="ctrl-section__val">{t.contrast}%</span>
                    </div>
                    <input type="range" className="ctrl-slider" min="0" max="300" value={t.contrast}
                        aria-label="Contrast" onChange={e => set({ contrast: +e.target.value })} />
                </div>

                {/* Brightness */}
                <div className="ctrl-section">
                    <div className="ctrl-section__title">
                        Brightness <span className="ctrl-section__val">{t.brightness}%</span>
                    </div>
                    <input type="range" className="ctrl-slider" min="0" max="300" value={t.brightness}
                        aria-label="Brightness" onChange={e => set({ brightness: +e.target.value })} />
                </div>

                {/* Color (RGB) */}
                <div className="ctrl-section">
                    <div className="ctrl-section__title">Color</div>
                    <div className="ctrl-rgb-row">
                        <label className="ctrl-rgb-label ctrl-rgb-label--r">R</label>
                        <input type="range" className="ctrl-slider ctrl-slider--r" min="-255" max="255" value={t.r}
                            aria-label="Red channel" onChange={e => set({ r: +e.target.value })} />
                        <span className="ctrl-rgb-val">{t.r > 0 ? `+${t.r}` : t.r}</span>
                    </div>
                    <div className="ctrl-rgb-row">
                        <label className="ctrl-rgb-label ctrl-rgb-label--g">G</label>
                        <input type="range" className="ctrl-slider ctrl-slider--g" min="-255" max="255" value={t.g}
                            aria-label="Green channel" onChange={e => set({ g: +e.target.value })} />
                        <span className="ctrl-rgb-val">{t.g > 0 ? `+${t.g}` : t.g}</span>
                    </div>
                    <div className="ctrl-rgb-row">
                        <label className="ctrl-rgb-label ctrl-rgb-label--b">B</label>
                        <input type="range" className="ctrl-slider ctrl-slider--b" min="-255" max="255" value={t.b}
                            aria-label="Blue channel" onChange={e => set({ b: +e.target.value })} />
                        <span className="ctrl-rgb-val">{t.b > 0 ? `+${t.b}` : t.b}</span>
                    </div>
                </div>

                {/* Reset All */}
                <button className="ctrl-reset-all" aria-label="Reset all transforms"
                    onClick={() => resetTransform(isSource)}>
                    <i className="fa fa-refresh" aria-hidden="true" /> Reset All
                </button>
            </div>
        );
    };



    // ── JSX ───────────────────────────────────────────────────────────────────
    return (
        <div className="compare-modal-overlay">
            <div className="compare-modal">
                {/* Hidden img elements — like Standardize's imgTempRef: loaded once by the browser,
                    reused for all canvas redraws (zoom/rotate/contrast/RGB) without re-fetching */}
                <img ref={sourceImgRef} alt="" style={{ display: 'none' }} />
                <img ref={targetImgRef} alt="" style={{ display: 'none' }} />

                {/* Header */}
                <div className="compare-header">
                    <h2>{language.compare?.title || 'Compare Items'}</h2>
                    <div className="compare-info">
                        <span className="target-info">
                            <strong>{language.compare?.analyzeItem || 'Analyze Item'}:</strong>{' '}
                            {probeItem ? `${probeItem.Make || 'Unknown'} ${probeItem.Model || ''}` : '--'}
                        </span>
                        <span className="source-info">
                            <strong>{language.compare?.searchResult || 'Search Result'}:</strong>{' '}
                            {resultItem ? `${resultItem.Make || 'Unknown'} ${resultItem.Model || ''}` : '--'}
                        </span>
                    </div>
                    <div className="view-mode-toggle">
                        <button
                            className={`vtb-btn${viewMode === 'side-by-side' ? ' vtb-btn--active' : ''}`}
                            onClick={() => setViewMode('side-by-side')}>
                            <i className="fa fa-columns" aria-hidden="true" />{' '}
                            {language.compare?.sideBySide || 'Side by Side'}
                        </button>
                        <button
                            className={`vtb-btn${viewMode === 'overlap' ? ' vtb-btn--active' : ''}`}
                            onClick={() => setViewMode('overlap')}>
                            <i className="fa fa-clone" aria-hidden="true" />{' '}
                            {language.compare?.overlap || 'Overlap'}
                        </button>
                    </div>
                    <button className="close-btn" onClick={onClose} aria-label="Close">&times;</button>
                </div>

                {/* Content */}
                <div className="compare-content">

                    {/* SIDE-BY-SIDE */}
                    {viewMode === 'side-by-side' && (<>
                        {/* Analyze Item - Left */}
                        <div className="image-container target-container">
                            <h3>{language.compare?.analyzeItemSection || 'Analyze Item'}</h3>
                            <div className="image-compare"
                                onMouseDown={e => handleMouseDown(e, 'target')}
                                style={{ cursor: targetTransform.moveLocked ? 'not-allowed' : 'grab' }}>
                                {targetLoading && (
                                    <div className="loading-overlay">
                                        <i className="fa fa-spinner fa-spin" />
                                        <p>{language.compare?.loadingImage || 'Loading...'}</p>
                                    </div>
                                )}
                                <canvas ref={targetCanvasRef} className="compare-canvas"
                                    onWheel={e => handleWheelZoom(e, false)}
                                    style={{ width: '100%', height: '100%', opacity: targetLoading ? 0.3 : 1 }} />
                                <button
                                    className={`ctrl-fab${openMenu === 'target' ? ' ctrl-fab--open' : ''}`}
                                    onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === 'target' ? null : 'target'); }}
                                    aria-label="Image controls" title="Image controls">
                                    <i className={`fa ${openMenu === 'target' ? 'fa-times' : 'fa-sliders'}`} aria-hidden="true" />
                                </button>
                                {openMenu === 'target' && <ControlPanel isSource={false} />}
                            </div>
                            <ThumbnailRow items={probeItem} activeIndex={targetImageIndex} isSource={false} onSelect={handleImageSelect} getThumbnailUrl={getImageThumbnailUrl} />
                        </div>

                        {/* Search Result - Right */}
                        <div className="image-container source-container">
                            <h3>{language.compare?.searchResultSection || 'Search Result'}</h3>
                            <div className="image-compare"
                                onMouseDown={e => handleMouseDown(e, 'source')}
                                style={{ cursor: sourceTransform.moveLocked ? 'not-allowed' : 'grab' }}>
                                {sourceLoading && (
                                    <div className="loading-overlay">
                                        <i className="fa fa-spinner fa-spin" />
                                        <p>{language.compare?.loadingImage || 'Loading...'}</p>
                                    </div>
                                )}
                                <canvas ref={sourceCanvasRef} className="compare-canvas"
                                    onWheel={e => handleWheelZoom(e, true)}
                                    style={{ width: '100%', height: '100%', opacity: sourceLoading ? 0.3 : 1 }} />
                                <button
                                    className={`ctrl-fab${openMenu === 'source' ? ' ctrl-fab--open' : ''}`}
                                    onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === 'source' ? null : 'source'); }}
                                    aria-label="Image controls" title="Image controls">
                                    <i className={`fa ${openMenu === 'source' ? 'fa-times' : 'fa-sliders'}`} aria-hidden="true" />
                                </button>
                                {openMenu === 'source' && <ControlPanel isSource={true} />}
                            </div>
                            <ThumbnailRow items={resultItem} activeIndex={sourceImageIndex} isSource={true} onSelect={handleImageSelect} getThumbnailUrl={getImageThumbnailUrl} />
                        </div>
                    </>)}

                    {/* OVERLAP */}
                    {viewMode === 'overlap' && (
                        <div className="image-container image-container--overlap">
                            <div className="overlap-slider-row">
                                <span className="overlap-label">{language.compare?.analyzeItemSection || 'Analyze Item'}</span>
                                <input type="range" min="0" max="100" value={overlapOpacity} className="overlap-slider"
                                    aria-label="Overlap opacity" onChange={e => setOverlapOpacity(+e.target.value)} />
                                <span className="overlap-label">{language.compare?.searchResultSection || 'Search Result'}</span>
                            </div>
                            <div className="image-compare image-compare--full"
                                onMouseDown={e => handleMouseDown(e, overlapOpacity >= 50 ? 'source' : 'target')}
                                style={{ cursor: 'grab' }}>
                                <canvas ref={overlapCanvasRef} className="compare-canvas"
                                    onWheel={e => handleWheelZoom(e, overlapOpacity >= 50)}
                                    style={{ width: '100%', height: '100%' }} />
                                <button
                                    className={`ctrl-fab ctrl-fab--left${openMenu === 'target' ? ' ctrl-fab--open' : ''}`}
                                    onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === 'target' ? null : 'target'); }}
                                    aria-label="Analyze Item controls" title="Analyze Item controls">
                                    <i className={`fa ${openMenu === 'target' ? 'fa-times' : 'fa-sliders'}`} aria-hidden="true" />
                                </button>
                                {openMenu === 'target' && <ControlPanel isSource={false} />}
                                <button
                                    className={`ctrl-fab${openMenu === 'source' ? ' ctrl-fab--open' : ''}`}
                                    onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === 'source' ? null : 'source'); }}
                                    aria-label="Search Result controls" title="Search Result controls">
                                    <i className={`fa ${openMenu === 'source' ? 'fa-times' : 'fa-sliders'}`} aria-hidden="true" />
                                </button>
                                {openMenu === 'source' && <ControlPanel isSource={true} />}
                            </div>
                            <div className="overlap-thumbnails">
                                <ThumbnailRow items={probeItem} activeIndex={targetImageIndex} isSource={false} onSelect={handleImageSelect} getThumbnailUrl={getImageThumbnailUrl} />
                                <ThumbnailRow items={resultItem} activeIndex={sourceImageIndex} isSource={true} onSelect={handleImageSelect} getThumbnailUrl={getImageThumbnailUrl} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Compare;